import React, { useState, useEffect } from 'react';
import { GiChurch } from 'react-icons/gi';
import { LuHeart, LuMail, LuPhone, LuMapPin, LuLoader } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { extractString, extractOrder } from '../../../shared/fieldUtils';

export default function RetiredEvangelists({ activeSection, onSelectSection }) {
  const [workersList, setWorkersList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchRetired() {
      try {
        let snap = await getDocs(collection(db, 'lights-of-diocese-members')).catch(() => null);
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'lights_of_diocese_members')).catch(() => null);
        }

        let list = [];
        if (snap && !snap.empty) {
          list = snap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(item => {
              const t = extractString(item.type || item.category || item.designation || item.role).toLowerCase();
              return t.includes('retired') && (t.includes('evangelist') || t.includes('worker'));
            })
            .map(data => ({
              id: data.id,
              name: extractString(data.name),
              email: extractString(data.email),
              ph: extractString(data.phoneno || data.phone || data.ph),
              place: extractString(data.place || data.parish || data.location),
              img: extractString(data.priestImg || data.img_url || data.image),
              order: extractOrder(data.order)
            }));
        }

        // Fallback
        if (list.length === 0) {
          let altSnap = await getDocs(collection(db, 'retired-evangelists')).catch(() => null);
          if (altSnap && !altSnap.empty) {
            list = altSnap.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                name: extractString(data.name),
                email: extractString(data.email),
                ph: extractString(data.phoneno || data.phone || data.ph),
                place: extractString(data.place || data.parish || data.location),
                img: extractString(data.priestImg || data.img_url || data.image),
                order: extractOrder(data.order)
              };
            });
          }
        }

        list.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        if (isMounted) setWorkersList(list);
      } catch (err) {
        console.error("Error fetching retired evangelists:", err);
        if (isMounted) setWorkersList([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchRetired();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Header Section */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 sm:pb-20 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] sm:right-[10%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 text-center md:text-left flex flex-col md:items-start items-center">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full"></div>
          <h1 className="font-serif font-bold tracking-[0.03em] text-[#7B1E3A] text-[38px] sm:text-[50px] md:text-[58px] lg:text-[64px] leading-[1.05] uppercase">
            Retired Evangelists & Church Workers
          </h1>
          <p className="mt-4 sm:mt-5 max-w-xl text-[#7B6B66] text-[15px] sm:text-[17px] leading-[1.6] font-normal">
            Honoring the devoted evangelists and church workers who gave their best years in the service of the Lord.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 mt-16 w-full space-y-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-[#E8DDD7]">
            <LuLoader className="w-10 h-10 text-[#7B1E3A] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Loading Retired Evangelists & Workers...</p>
          </div>
        ) : workersList.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[24px] border border-[#E8DDD7] p-8">
            <h3 className="font-serif font-bold text-xl text-[#7B1E3A]">No Retired Evangelists & Church Workers Found</h3>
            <p className="text-sm text-[#7B6B66] mt-1">Please check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {workersList.map((person, idx) => (
              <div key={person.id || idx} className="bg-white p-6 rounded-[24px] border border-[#E8DDD7] shadow-[0_10px_30px_rgba(123,30,58,0.04)] group hover:-translate-y-1 transition-transform flex flex-col items-start text-left">
                {person.img ? (
                  <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-sm border border-[#E8DDD7] mb-4 shrink-0 bg-[#FAF6F2]">
                    <img src={person.img} alt={person.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : (
                  <div className="w-14 h-14 shrink-0 bg-[#FAF6F2] text-[#7B1E3A] rounded-full flex items-center justify-center group-hover:bg-[#7B1E3A] group-hover:text-white transition-colors mb-4">
                    <LuHeart className="w-7 h-7" />
                  </div>
                )}
                <div className="w-full">
                  <h3 className="font-serif font-bold text-[#7B1E3A] text-lg mb-4">{person.name}</h3>
                  <div className="space-y-3 text-[#5F5A58] text-sm">
                    {person.place && (
                      <div className="flex items-start gap-2.5">
                        <LuMapPin className="w-4 h-4 text-[#D4A64A] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{person.place}</span>
                      </div>
                    )}
                    {person.ph && person.ph !== "0" && person.ph !== "NA" && (
                      <div className="flex items-center gap-2.5">
                        <LuPhone className="w-4 h-4 text-[#D4A64A] shrink-0" />
                        <a href={`tel:${person.ph}`} className="leading-relaxed hover:text-[#7B1E3A] transition-colors">{person.ph}</a>
                      </div>
                    )}
                    {person.email && person.email !== "NA" && person.email !== "." && (
                      <div className="flex items-start gap-2.5">
                        <LuMail className="w-4 h-4 text-[#D4A64A] shrink-0 mt-0.5" />
                        <a href={`mailto:${person.email.trim()}`} className="hover:text-[#7B1E3A] transition-colors break-words whitespace-normal leading-relaxed">{person.email}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Navigation */}
        <div className="text-center pb-8 pt-8">
          <button 
            onClick={() => onSelectSection('lights')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#7B1E3A] hover:bg-[#5E132A] text-white font-sans font-bold text-sm tracking-wider uppercase rounded-full transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Return to Lights of Diocese
          </button>
        </div>
      </div>
    </div>
  );
}
